import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Steps, Button, Upload, App } from 'antd';
import { createAssetOrder, updateAssetOrderWithOrderableItems, updateAssetOrderForQuote } from '../../services/api/catalogOrdersApi';
import StepperModal from '../Components/Modals/StepperModal';
import UploadFileStep from './Steps/UploadFileStep';
import OrderReviewStep from './Steps/OrderReviewStep';
import AdditionalInfoStep from './Steps/AdditionalInfoStep';
import ConfirmCloseModal from './Modals/ConfirmCloseModal';
import ConfirmOrderModal from './Modals/ConfirmOrderModal';
import SuccessOrderModal from './Modals/SuccessOrderModal';


const { Step } = Steps;

/**
 * Modal component for bulk ordering 3D assets
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.visible - Determines if the modal is visible
 * @param {Function} props.onCancel - Function called when the modal is closed
 * @param {boolean} props.resetStepper - Flag to reset the stepper to initial state
 * @param {Function} props.onResetComplete - Callback when reset is complete
 * @param {string} props.clientId - Client ID for the asset order
 * @param {Function} props.getClientId - Function to get client ID when needed
 * @param {Object} props.initialData - Initial data to preload (from BulkAddModal)
 * @param {number} props.initialStep - Initial step to start from (0 = first step, 1 = second step)
 */
const BulkOrderModal = ({
    visible,
    onCancel,
    onDirectClose = null,
    resetStepper = false,
    onResetComplete = () => { },
    clientId = null,
    getClientId = null,
    initialData = null,
    initialStep = 0
}) => {
    const { message } = App.useApp();
    const [currentStep, setCurrentStep] = useState(() => {
        return initialStep;
    });
    const [fileList, setFileList] = useState([]);
    const [uploadedData, setUploadedData] = useState([]);
    const [summary, setSummary] = useState({
        total: 0,
        orderable: 0,
        error: 0,
        inProgress: 0,
        details: []
    });
    const [processingUpload, setProcessingUpload] = useState(false);
    const [parsingFile, setParsingFile] = useState(false);
    const [fileProcessed, setFileProcessed] = useState(false);
    const [assetOrderId, setAssetOrderId] = useState(null);
    const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
    const [confirmOrderVisible, setConfirmOrderVisible] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [successModalProductsCount, setSuccessModalProductsCount] = useState(0);
    const [selectedProductsCount, setSelectedProductsCount] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [internalVisible, setInternalVisible] = useState(true);
    const [additionalInfo, setAdditionalInfo] = useState({
        batch: '',
        name: '',
        image: ''
    });

    const [savingToOrder, setSavingToOrder] = useState(false);

    // Effect to handle initial data from BulkAddModal
    useEffect(() => {
        if (visible && initialData && initialStep > 0) {

            // Set the data from BulkAddModal
            setUploadedData(initialData.results || []);
            setSummary(initialData.summary || {
                total: 0,
                orderable: 0,
                error: 0,
                inProgress: 0,
                details: []
            });

            // Mark file as processed since we have data
            setFileProcessed(true);

            // Set the initial step
            setCurrentStep(initialStep);

            // Create asset order for the transferred data ONLY if we don't already have one
            if (!assetOrderId) {
                handleInitialDataOrder();
            }
        }
    }, [visible, initialData, initialStep, assetOrderId]);

    // Effect to reset the stepper when the resetStepper prop changes
    useEffect(() => {
        if (resetStepper && visible) {

            // Reset all state to initial values
            setCurrentStep(initialStep);
            setFileList([]);
            setUploadedData([]);
            setSummary({
                total: 0,
                orderable: 0,
                error: 0,
                inProgress: 0,
                details: []
            });
            setFileProcessed(false);
            setAssetOrderId(null);
            setConfirmCloseVisible(false);
            setProcessingUpload(false);
            setParsingFile(false);
            setSelectedRowKeys([]);
            setSelectedProductsCount(0);
            setSelectedProducts([]);
            setInternalVisible(true);
            setAdditionalInfo({
                batch: '',
                name: '',
                image: ''
            });
            setSavingToOrder(false);

            // Notify parent that reset is complete
            onResetComplete();
        }
    }, [resetStepper, visible, onResetComplete, initialStep]);

    // Effect to initialize selections when initial data is loaded
    useEffect(() => {
        if (initialData && summary.details?.orderable && summary.details.orderable.length > 0) {

            // Select all orderable items by default
            const allOrderableKeys = summary.details.orderable.map(item => getRowKey(item));
            setSelectedRowKeys(allOrderableKeys);
            setSelectedProducts(summary.details.orderable);
            setSelectedProductsCount(summary.details.orderable.length);

        }
    }, [summary.details?.orderable, initialData]);

    // Effect to reset internal visibility when the modal becomes visible
    useEffect(() => {
        if (visible) {
            setInternalVisible(true);
        }
    }, [visible, initialStep, initialData, currentStep]);

    // Configuration for file upload
    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.csv, .xlsx, .xls',
        fileList,
        beforeUpload: (file) => {
            // Verify that the file is in a supported format
            const isValidFormat = file.type === 'text/csv' ||
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel';

            if (!isValidFormat) {
                message.error('You can only upload CSV or Excel files!');
                return Upload.LIST_IGNORE;
            }

            // Add the file to the list
            setFileList([file]);

            // Reset file processed state when a new file is uploaded
            setFileProcessed(false);

            // Prevent automatic upload
            return false;
        },
        onRemove: () => {
            setFileList([]);
            setUploadedData([]);
            setSummary({
                total: 0,
                orderable: 0,
                error: 0,
                inProgress: 0,
                details: []
            });
            setFileProcessed(false);
            setAssetOrderId(null);
        }
    };

    // Function to handle file upload (creates asset order)
    const handleFileUploaded = async (file) => {
        // Skip creating order if we already have one (from initial data)
        if (assetOrderId) {
            return;
        }

        try {
            // Get clientId from function if available, otherwise use direct prop
            const currentClientId = getClientId ? await getClientId() : clientId;

            if (!currentClientId || currentClientId === '' || currentClientId === 'null') {
                console.warn('No valid client ID provided, cannot create asset order. ClientId:', currentClientId);
                message.warning('Client ID not available. Please try logging in again.');
                return;
            }

            const orderId = await createAssetOrder(currentClientId);
            setAssetOrderId(orderId);
        } catch (error) {
            console.error('Error creating asset order:', error);
            message.error('Error creating asset order');
        }
    };

    // Function to handle file processing result
    const handleFileProcessed = async (success, data) => {
        if (success) {
            setFileProcessed(true);

            // Update uploaded data with API results
            if (data.results) {
                setUploadedData(data.results);

                // Calculate summary based on new result fields - filter for order flow
                const orderableItems = data.results.filter(item => item.result === 'Orderable');
                const errorItems = data.results.filter(item => item.result === 'Error');
                const inProgressItems = data.results.filter(item => item.result === 'In Progress');

                setSummary({
                    total: data.results.length,
                    orderable: orderableItems.length,
                    error: errorItems.length,
                    inProgress: inProgressItems.length,
                    details: {
                        orderable: orderableItems,
                        error: errorItems,
                        inProgress: inProgressItems
                    }
                });

                // Save Orderable and Error items to the asset order ONLY if we have an order ID and not already saving
                if (assetOrderId && data.results.length > 0 && !savingToOrder) {
                    setSavingToOrder(true); // Prevent multiple calls
                    try {
                        await updateAssetOrderWithOrderableItems(assetOrderId, data.results);
                    } catch (error) {
                        console.error('❌ Error saving items to asset order:', error);
                        message.error('Error saving items to asset order');
                        setFileProcessed(false);
                        return;
                    } finally {
                        setSavingToOrder(false); // Reset the flag
                    }
                } else if (!assetOrderId) {
                    console.warn('No asset order ID available to save processed data');
                } else if (savingToOrder) {
                    console.warn('Already saving to asset order, skipping duplicate call');
                }
            }

            message.success('File processed successfully!');

            // Automatically go to the second step after successful processing
            setTimeout(() => {
                setCurrentStep(1);
            }, 500);
        } else {
            setFileProcessed(false);
            // Error message is already handled by UploadFileStep component
        }
    };

    // Function to handle modal close attempt
    const handleCloseAttempt = () => {
        // Check if there's unsaved data
        const hasUnsavedData = fileList.length > 0 || currentStep > 0 || uploadedData.length > 0 || assetOrderId;

        if (hasUnsavedData) {
            setConfirmOrderVisible(false);
            setSuccessModalVisible(false);
            setConfirmCloseVisible(true);
        } else {
            handleConfirmClose();
        }
    };

    // Function to handle confirmed close
    const handleConfirmClose = () => {
        setInternalVisible(false);
        setConfirmCloseVisible(false);

        // Complete reset of all data
        setCurrentStep(initialStep);
        setFileList([]);
        setUploadedData([]);
        setSummary({
            total: 0,
            orderable: 0,
            error: 0,
            inProgress: 0,
            details: []
        });
        setProcessingUpload(false);
        setParsingFile(false);
        setFileProcessed(false);
        setAssetOrderId(null);
        setSelectedRowKeys([]);
        setSelectedProductsCount(0);
        setSelectedProducts([]);
        setAdditionalInfo({
            batch: '',
            name: '',
            image: ''
        });
        setSavingToOrder(false);

        // Use onDirectClose if available to avoid triggering close logic again, otherwise use onCancel
        if (onDirectClose) {
            onDirectClose();
        } else {
            onCancel();
        }
    };

    // Function to cancel the close operation
    const handleCancelClose = () => {
        setConfirmCloseVisible(false);
    };

    // Function to process file from button click
    const handleProcessFile = async () => {
        if (!fileList.length) {
            message.error('Please upload a file first');
            return;
        }

        // Trigger the file processing from UploadFileStep
        if (uploadStepRef.current && uploadStepRef.current.processFile) {
            uploadStepRef.current.processFile();
        }
    };

    // Function to go back to the previous step
    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    // Function to proceed to next step
    const nextStep = () => {
        setCurrentStep(currentStep + 1);
    };

    // Function to handle additional info changes
    const handleAdditionalInfoChange = useCallback((newAdditionalInfo) => {
        setAdditionalInfo(newAdditionalInfo);
    }, []);

    // Function to generate custom order ID
    const generateOrderId = () => {
        const currentYear = new Date().getFullYear().toString().slice(-2); // Last 2 digits of year
        const randomNumbers = Math.floor(100000 + Math.random() * 900000); // 6 random digits
        return `Order_M3D_${randomNumbers}_${currentYear}`;
    };

    // Function to show confirmation modal
    const handleFinish = () => {
        if (selectedProductsCount === 0) {
            message.error('Please select at least one item to order');
            return;
        }

        // Force update additional info from form before validation
        if (additionalInfoRef.current && additionalInfoRef.current.updateData) {
            additionalInfoRef.current.updateData();
        }

        // Small delay to ensure data is updated before validation
        setTimeout(() => {
            // Check if batch is required and provided
            if (!additionalInfo.batch || additionalInfo.batch.trim() === '') {
                message.error('Please enter a batch identifier before requesting a quote');
                return;
            }

            // Close other modals before opening order confirmation
            setConfirmCloseVisible(false);
            setSuccessModalVisible(false);
            setConfirmOrderVisible(true);
        }, 50);
    };

    // Function to complete the process after confirmation
    const handleConfirmOrder = async () => {
        setProcessingUpload(true);

        // Save necessary data before reset
        const currentAssetOrderId = assetOrderId;
        const currentSelectedProducts = [...selectedProducts];
        const currentSelectedProductsCount = selectedProductsCount;

        // Validate required data first
        if (!currentAssetOrderId || !currentSelectedProducts.length) {
            message.error('Missing required data for asset order update. Please try uploading your file again.');
            setConfirmOrderVisible(false);
            setProcessingUpload(false);
            return;
        }

        try {
            // Generate custom order ID
            const customOrderId = generateOrderId();

            // Prepare order data with additional info
            const orderData = {
                products: currentSelectedProducts,
                additionalInfo: {
                    ...additionalInfo,
                    orderId: customOrderId
                }
            };

            // Update the asset order with final selections, status, and additional info
            await updateAssetOrderForQuote(currentAssetOrderId, orderData);

            // Show success immediately after updating the order
            setConfirmOrderVisible(false);
            setConfirmCloseVisible(false);
            setSuccessModalProductsCount(currentSelectedProductsCount);
            setSuccessModalVisible(true);
            setProcessingUpload(false);

            // Call Firebase function in background without waiting for result
            setTimeout(async () => {
                try {
                    console.log('🚀 Calling Firebase function with orderId:', currentAssetOrderId);
                    console.log('🔗 Function URL:', process.env.REACT_APP_DOWNLOAD_VARIANT_JSON_HTTP);

                    const response = await fetch(process.env.REACT_APP_DOWNLOAD_VARIANT_JSON_HTTP, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            orderId: currentAssetOrderId
                        })
                    });

                    console.log('📡 Firebase function response status:', response.status);

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('❌ Firebase function error response:', errorText);
                        console.error('📊 Full response details:', {
                            status: response.status,
                            statusText: response.statusText,
                            headers: Object.fromEntries(response.headers.entries())
                        });

                        // Don't show error to user - this is a background process
                        console.warn('Background processing failed. Order was saved successfully but post-processing failed.');
                        return;
                    }

                    console.log('✅ Firebase function completed successfully');
                } catch (firebaseError) {
                    console.error('❌ Error calling Firebase function in background:', firebaseError);
                    console.error('📊 Full error details:', {
                        message: firebaseError.message,
                        stack: firebaseError.stack
                    });

                    // Don't show error to user - this is a background process
                    console.warn('Background processing failed. Order was saved successfully but post-processing failed.');
                }
            }, 2000); // Increased delay to ensure order is fully saved

        } catch (error) {
            console.error('Error updating asset order:', error);
            message.error('Failed to update asset order. Please try again.');
            setConfirmOrderVisible(false);
            setProcessingUpload(false);
            return;
        }
    };

    // Function to cancel the order operation
    const handleCancelOrder = () => {
        setConfirmOrderVisible(false);
    };



    // Function to handle success modal close
    const handleSuccessModalClose = () => {
        setSuccessModalVisible(false);
        setSuccessModalProductsCount(0);

        // Complete reset and close main modal
        setInternalVisible(false);
        setConfirmOrderVisible(false);
        setConfirmCloseVisible(false);

        // Complete state reset
        setCurrentStep(initialStep);
        setFileList([]);
        setUploadedData([]);
        setSummary({
            total: 0,
            orderable: 0,
            error: 0,
            inProgress: 0,
            details: []
        });
        setFileProcessed(false);
        setAssetOrderId(null);
        setSelectedProductsCount(0);
        setSelectedProducts([]);
        setSelectedRowKeys([]);
        setAdditionalInfo({
            batch: '',
            name: '',
            image: ''
        });

        // Call parent cleanup functions to close modal and refresh orders
        if (onDirectClose) {
            onDirectClose();
        } else if (onCancel) {
            onCancel();
        }
    };

    // Function to get row key for items
    const getRowKey = (record) => {
        return record.skuCode || record.eanCode || record.upcCode || `item-${record.index || 'unknown'}`;
    };

    // Function to handle selection changes from OrderReviewStep
    const handleSelectionChange = useCallback((newSelectedRowKeys) => {
        // Ensure newSelectedRowKeys is an array
        const selectedKeys = Array.isArray(newSelectedRowKeys) ? newSelectedRowKeys : [];

        setSelectedRowKeys(selectedKeys);

        // Calculate selected products based on available data
        const orderableItems = summary.details?.orderable || [];
        const selectedItems = orderableItems.filter(item => {
            const key = getRowKey(item);
            return selectedKeys.includes(key);
        });

        setSelectedProductsCount(selectedItems.length);
        setSelectedProducts(selectedItems);

    }, [summary.details?.orderable]);

    // Ref for UploadFileStep component
    const uploadStepRef = useRef(null);

    // Ref for AdditionalInfoStep component
    const additionalInfoRef = useRef(null);

    // Content for each step
    const steps = [
        {
            title: 'Upload File',
            content: <UploadFileStep
                ref={uploadStepRef}
                uploadProps={uploadProps}
                parsingFile={parsingFile}
                onFileProcessed={handleFileProcessed}
                onFileUploaded={handleFileUploaded}
            />
        },
        {
            title: 'Review & Order',
            content: (
                <OrderReviewStep
                    summary={summary}
                    uploadedData={uploadedData}
                    parsingFile={parsingFile}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChange={handleSelectionChange}
                />
            )
        },
        {
            title: 'Additional Info',
            content: (
                <AdditionalInfoStep
                    ref={additionalInfoRef}
                    onDataChange={handleAdditionalInfoChange}
                    initialData={additionalInfo}
                    assetOrderId={assetOrderId}
                />
            )
        }
    ];

    // Bulk Order Stepper component
    const BulkOrderStepper = () => (
        <div style={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '1.5rem', height: '35rem', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                {/* Left section with steps */}
                <div style={{ width: '15.625rem', flexShrink: 0, borderRight: '1px solid #f0f0f0', paddingRight: '1rem' }}>
                    <Steps current={currentStep} direction="vertical" style={{ height: '100%' }}>
                        {steps.map(item => (
                            <Step key={item.title} title={item.title} />
                        ))}
                    </Steps>
                </div>

                {/* Main content */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: 'calc(100% - 17rem)', boxSizing: 'border-box', overflow: 'hidden' }}>
                    <div className="steps-content" style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', width: '100%', boxSizing: 'border-box' }}>
                        {steps[currentStep].content}
                    </div>
                </div>
            </div>

            <div className="steps-action" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                    {currentStep > 0 && !(initialData && currentStep === initialStep) && (
                        <Button onClick={prevStep} disabled={processingUpload}>
                            Previous
                        </Button>
                    )}
                </div>

                <div>
                    {currentStep === 0 && (
                        <>
                            <Button onClick={handleCloseAttempt} style={{ marginRight: '8px' }}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleProcessFile}
                                disabled={!fileList.length || parsingFile || processingUpload}
                                loading={parsingFile}
                            >
                                Process File
                            </Button>
                        </>
                    )}

                    {currentStep === 1 && (
                        <Button
                            type="primary"
                            onClick={nextStep}
                            disabled={selectedProductsCount === 0 || processingUpload}
                        >
                            Continue
                        </Button>
                    )}

                    {currentStep === steps.length - 1 && (
                        <Button
                            type="primary"
                            onClick={handleFinish}
                            loading={processingUpload}
                            disabled={processingUpload || !additionalInfo.batch || additionalInfo.batch.trim() === ''}
                        >
                            Request Quote
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );

    // Function to create asset order for initial data
    const handleInitialDataOrder = async () => {
        // Double check that we don't already have an order
        if (assetOrderId) {
            return;
        }

        // Also check if we're already saving to prevent race conditions
        if (savingToOrder) {
            return;
        }

        setSavingToOrder(true); // Prevent multiple calls
        try {
            // Get clientId from function if available, otherwise use direct prop
            const currentClientId = getClientId ? await getClientId() : clientId;

            if (!currentClientId || currentClientId === '' || currentClientId === 'null') {
                console.warn('No valid client ID provided for initial data order');
                return;
            }

            const orderId = await createAssetOrder(currentClientId);
            setAssetOrderId(orderId);

            // Save the initial data to the asset order
            if (initialData?.results && initialData.results.length > 0) {
                try {
                    await updateAssetOrderWithOrderableItems(orderId, initialData.results);
                } catch (error) {
                    console.error('❌ Error saving initial data to asset order:', error);
                    message.error('Error saving initial data to asset order');
                }
            }
        } catch (error) {
            console.error('Error creating asset order for initial data:', error);
            message.error('Error creating asset order for initial data');
        } finally {
            setSavingToOrder(false); // Reset the flag
        }
    };

    return (
        <>
            <StepperModal
                title="Bulk Order 3D Assets"
                visible={visible && internalVisible && !confirmCloseVisible && !confirmOrderVisible && !successModalVisible}
                onCancel={handleCloseAttempt}
            >
                <BulkOrderStepper />
            </StepperModal>

            {/* Confirm close modal */}
            <ConfirmCloseModal
                visible={confirmCloseVisible && !confirmOrderVisible && !successModalVisible}
                onCancel={handleCancelClose}
                onConfirm={handleConfirmClose}
            />

            {/* Confirm order modal */}
            <ConfirmOrderModal
                visible={confirmOrderVisible && !confirmCloseVisible && !successModalVisible}
                onCancel={handleCancelOrder}
                onConfirm={handleConfirmOrder}
                selectedCount={selectedProductsCount}
                loading={processingUpload}
            />

            {/* Success modal */}
            <SuccessOrderModal
                visible={successModalVisible && !confirmCloseVisible && !confirmOrderVisible}
                onClose={handleSuccessModalClose}
                productsCount={successModalProductsCount}
            />
        </>
    );
};

export default BulkOrderModal; 